export enum SyncAndRecurrenceEmit {
    OnlySync = 1,
    OnlyRecurrence = 2,
    SyncAndRecurrence = 3
}

export interface CategoryBase {
    Id: string;
    Name: string;
    Description: string;
}

export interface Category extends CategoryBase {
    SupporterCategories: Array<SupporterCategory>;
}

export interface CategoryDto extends CategoryBase {
    Supporters: Array<SupporterDto>;
    Default: boolean;
}

export interface SupporterCategory {
    SupporterId: string;
    Supporter: Supporter;
    CategoryId: string;
    Category: Category;
}

export interface SupporterDto {
    Id: string;
    FirstName: string;
    LastName: string;
    Email: string;
    IsRemoved: boolean;
}

export interface Supporter {
    Id: string;
    UserId: string;
    User: OktaUser;
    TeamId: string;
    Team: Team;
    IsRemoved: boolean;
}

export interface OktaUser {
    Id: string;
    Email: string;
}

export enum UserRole {
    Admin = 100,
    Technician = 50,
    User = 10
}

export enum Division {
    ConditionsNotFulfilled = 0,
    ThermalEurope = 1,
    GlobalSupport = 2
}

export interface Team {
    Id: string;
    Name: string;
    Location: string;
}

export interface TeamWithMembersDto {
    TeamId: string;
    Supporters: Array<SupporterEditDto>;
}

export interface Language {
    Id: string;
    Name: string;
    LanguageCode: LanguageCode;
}

export enum LanguageCode {
    en = 1,
    fr = 2,
    de = 3,
    es = 4,
    nl = 5,
    it = 6
}

export interface KeywordDto {
    Id?: string;
    Name: string;
    CategoryId: string;
    CategoryName: string;
    LanguageCode: LanguageCode;
}

export interface MeetingDto {
    Id?: number;
    AvailabilityId?: number;
    Type: MeetingType;
    Status: MeetingStatus;
    CategoryId: string;
    LanguageId: string;
    Keyword: string;
    MismatchedKeyword: string;
    PhoneNumber: string;
    ProblemDescription: string;
    AdditionalInformation: string;
}

export interface MeetingDetailsDto extends MeetingDto {
    Start: Date;
    End: Date;
    UserId: string;
    Solved: boolean;
    SupporterId: string;
    CategoryName: string;
    LanguageName: string;
    UserEmail: string;
    SupporterFirstName: string;
    SupporterLastName: string;
    UserFirstName: string;
    UserLastName: string;
}

export interface Meeting extends MeetingDetailsDto {
    Supporter: Supporter;
    User: OktaUser;
    Category: Category;
    Language: Language;
}

export interface MeetingRow {
    Id: number;
    CategoryName: string;
    CategoryId: string;
    LanguageName: string;
    LanguageId: string;
    Time: string;
    Date: string;
    DayNumber: string;
    Status: MeetingStatus;
    Keyword: string;
    MismatchedKeyword?: string;
    ProblemDescription: string;
    AdditionalInformation?: string;
    Type: MeetingType;
    PhoneNumber: string;
}

export enum MeetingType {
    Teams = 1,
    Phone = 2
}

export enum MeetingStatus {
    Active = 1,
    Cancel = 2
}

export interface AvailabilityBase {
    Start: Date;
    End: Date;
    SupporterId: string;
}

export interface AvailabilityDto extends AvailabilityBase {
    Id?: number;
    Taken: boolean;
}

export interface AvailabilitySlotDto extends AvailabilityBase {
    Id: number;
    LanguageId: string;
    CategoryId: string;
    LanguageName: string;
    CategoryName: string;
}

export interface AvailabilitySlotDetailsDto {
    Id: number;
    Start: Date;
    End: Date;
    SupporterId: string;
    SupporterFirstName: string;
    SupporterLastName: string;
    CategoryIdCollection: Array<string>;
    LanguageIdCollection: Array<string>;
}

export interface SolvedMeetingDto {
    Id: number;
    Solved: boolean;
}

export interface SelectedSlotDto {
    Id: number;
    Date: Date;
    TimeSlot: string;
    Type: string;
    SupporterId: string;
    Category: CategoryBase;
    Language: Language;
    MeetingType: MeetingType;
    PhoneNumber: string;
    ProblemDescription: string;
}

export interface FirstStepData {
    Submit: boolean;
    Category: CategoryDto;
    Keyword?: KeywordDto;
    MismatchedKeyword?: string;
    ProblemDescription?: string;
}

export interface UserDetailsDto extends OktaUser {
    UserRole: UserRole;
    FirstName: string;
    LastName: string;
    Division: Division;
}

export interface TranslationDto {
    CategoryId: string;
    Translations: Array<TranslationDetailsDto>;
}

export interface TranslationDetailsDto {
    Id?: string;
    KeywordId: string;
    English: string;
    French: string;
    German: string;
    Spanish: string;
    Dutch: string;
    Italian: string;
}

export interface EditTranslationDto {
    CategoryId: string;
    TranslationDetails: TranslationDetailsDto;
}

export interface CategoryTranslation extends CategoryTranslationDto {
    Category: Category;
}

export interface CategoryTranslationDto {
    LanguageId: string;
    Name: string;
    Description: string;
}

export interface CategoryTranslationCollectionDto {
    CategoryId: string;
    Translations: CategoryTranslationDto[];
}

export interface DescriptionDto {
    Id: number;
    Text: string;
    MismatchedKeyword: string;
    LanguageName: string;
    CreatedAt: Date;
}

export interface GDPREntry {
    HtmlText: string;
    LanguageCode: string;
    LanguageName: string;
}

export interface DisabledSlot {
    Start: Date;
}

export interface NewSlots {
    Slots: AvailabilityDto[];
    SupporterId: string;
}

export interface FilterableTranslation {
    KeywordId: string;
    Translations: Array<string>;
}

export interface TeamsWithSupportersDto {
    Id: string;
    Name: string;
    Supporters:  Array<SupporterForCollectionDto>;
}

export interface SupporterForCollectionDto {
    Id: string;
    Name: string;
}

export interface SupporterEditDto extends SupporterDto {
    CategoryIds: Array<string>;
    LanguageIds: Array<string>;
    TeamId: string;
}

export interface SupporterFilters {
    SelectedLanguages: Array<string>;
    SelectedCategory: Array<string>;
    SelectedSupporters: Array<string>;
    ShowAvailability: boolean;
}

export interface SupporterFiltersCached extends SupporterFilters {
    CheckedKeyIndexes: Array<string>;
}

export class TranslationCollection {
    public LanguageCode: LanguageCode;
    public Collection: TranslationSet;
 }

export class TranslationSet {
    public LanguageCode: LanguageCode;
    public Values: {[key: string]: string} = {};
 }

 export class TemplateDto {
    TemplateString: string;
    Translations: {[key: string]: string} = {};
}

export enum EmailType {
    CreateAppointment = 1,
    UpdateAppointment = 2,
    CancelAppointment = 3,
    ProfileChange = 4,
    AvailabilityChange = 5,
    NewAvailability = 6,
    RemovedAvailability = 7
}

export class EmailTranslationDto {
    LanguageId: string;
    EmailType: EmailType;
    LanguageCode: LanguageCode;
    Text: string;
    Title: string;
}

export interface FilterMatch {
    Keyword: KeywordDto;
    InputFullyIncluded: boolean;
    FullyContainedKeywords: number;
    MatchedCharacters: number;
    IndexOfMatch: number;
}

export interface LanguageTranslationDto {
    TranslatedLanguageId: string;
    LanguageId: string;
    Translation: string;
}
